import { ethers } from 'ethers'
import { Contract } from 'ethers'
import type { MarketplaceSale } from '@mprolab/sdk'
import { MARKETPLACE_ABI } from './abi'

/**
 * Reads completed sales from the marketplace's `Bought` and `OfferAccepted`
 * events.
 *
 * Events rather than storage, deliberately: a listing that is no longer active
 * was either bought or cancelled, and storage cannot tell those apart. The
 * event is the only record that says a sale happened, who bought, and what the
 * platform fee was.
 *
 * Both events, necessarily: a licence changes hands in two directions. A buyer
 * takes a listing (`Bought`) or a holder fills a standing offer
 * (`OfferAccepted`), and both are a completed sale to everyone looking at the
 * tab. Reading only `Bought` made every offer-side sale invisible — the market
 * showed one finished trade where two had settled.
 */

/** Public RPCs cap eth_getLogs ranges; 9k blocks is under every limit we hit. */
const CHUNK = 9000

/**
 * Ceiling on how far back a single page load will scan. Base produces a block
 * every 2s, so this is roughly the last two weeks. Reaching it is reported to
 * the caller rather than silently showing a partial history as a whole one.
 */
const MAX_CHUNKS = 80

/**
 * Sales already read, kept per marketplace address so a later visit scans only
 * the blocks since the last one.
 *
 * Without this the cost of the tab grows with the age of the contract: every
 * open would re-read the whole history, one sequential request per 9k blocks,
 * and a public RPC starts answering 429 long before that finishes. Sales are
 * immutable once mined, so re-reading them buys nothing.
 */
/*
 * v2 because v1 recorded only `Bought`. The cache resumes from its last
 * scanned block, so a returning browser would carry its listing-only history
 * forward and never look back for the offer sales it had missed. A new key
 * retires those entries instead of trusting them.
 */
const CACHE_KEY = 'mprolab.marketplace.sales.v2'

interface Cached {
  lastScannedBlock: number
  sales: MarketplaceSale[]
}

function readCache(marketplaceAddress: string): Cached | null {
  try {
    const raw = window.localStorage.getItem(`${CACHE_KEY}.${marketplaceAddress}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Cached
    if (typeof parsed?.lastScannedBlock !== 'number' || !Array.isArray(parsed.sales)) {
      return null
    }
    return parsed
  } catch {
    // Private mode, cleared storage, a corrupt entry — all mean "no cache".
    return null
  }
}

function writeCache(marketplaceAddress: string, value: Cached): void {
  try {
    window.localStorage.setItem(`${CACHE_KEY}.${marketplaceAddress}`, JSON.stringify(value))
  } catch {
    // Storage being unavailable must never break the page.
  }
}

export interface SalesResult {
  sales: MarketplaceSale[]
  /** True when the scan stopped short of the deployment block. */
  truncated: boolean
  /** Oldest block actually scanned. */
  scannedFrom: number
}

export async function readSales(params: {
  marketplaceAddress: string
  provider: ethers.providers.Provider
  deployedAtBlock: number
}): Promise<SalesResult> {
  const market = new Contract(
    params.marketplaceAddress,
    MARKETPLACE_ABI as unknown as ethers.ContractInterface,
    params.provider
  )
  const latest = await params.provider.getBlockNumber()
  const cached = readCache(params.marketplaceAddress)
  // Resume from where the last visit finished; a sale already mined cannot
  // change, so there is nothing to re-read below that point.
  const start = cached ? cached.lastScannedBlock + 1 : params.deployedAtBlock
  const floor = Math.max(start, latest - CHUNK * MAX_CHUNKS)
  const truncated = floor > params.deployedAtBlock && !cached

  /*
   * One request per window covering both events, not one per event.
   *
   * `eth_getLogs` accepts an array in a topic position and reads it as OR, so
   * a single call returns `Bought` and `OfferAccepted` together. Querying them
   * separately doubles the requests against a public RPC, and this scan
   * already walks up to 80 windows — the second series is what turns a scan
   * that works into 429s. The same rate limiting has cost this project a
   * withheld payout and two false probe alarms; it is not a theoretical risk.
   */
  const boughtTopic = market.interface.getEventTopic('Bought')
  let offerTopic: string | null = null
  try {
    // Absent on a pre-upgrade contract, which must degrade to "no offer
    // sales" rather than losing the listing history alongside it.
    offerTopic = market.interface.getEventTopic('OfferAccepted')
  } catch {
    offerTopic = null
  }
  const topics = [offerTopic ? [boughtTopic, offerTopic] : [boughtTopic]]

  const sales: MarketplaceSale[] = []

  for (let to = latest; to >= floor; to -= CHUNK) {
    const from = Math.max(floor, to - CHUNK + 1)
    let logs: ethers.providers.Log[] = []
    try {
      logs = await params.provider.getLogs({
        address: params.marketplaceAddress,
        topics,
        fromBlock: from,
        toBlock: to
      })
    } catch {
      // A provider refusing one window must not lose the rest of the history.
      continue
    }

    for (const log of logs) {
      let parsed: ethers.utils.LogDescription
      try {
        parsed = market.interface.parseLog(log)
      } catch {
        // An event this build does not know about is not a sale.
        continue
      }
      const a = parsed.args
      if (parsed.name === 'Bought') {
        sales.push({
          source: 'listing',
          sourceId: a.listingId.toString(),
          seller: String(a.seller).toLowerCase(),
          buyer: String(a.buyer).toLowerCase(),
          nft: String(a.nft).toLowerCase(),
          tokenId: a.tokenId.toString(),
          priceWei: a.price.toString(),
          feeWei: a.fee.toString(),
          blockNumber: log.blockNumber,
          txHash: log.transactionHash
        })
      } else if (parsed.name === 'OfferAccepted') {
        sales.push({
          source: 'offer',
          sourceId: a.offerId.toString(),
          seller: String(a.seller).toLowerCase(),
          buyer: String(a.buyer).toLowerCase(),
          nft: String(a.nft).toLowerCase(),
          tokenId: a.tokenId.toString(),
          // The offer's unit price is what this one licence sold for.
          priceWei: a.unitPrice.toString(),
          feeWei: a.fee.toString(),
          blockNumber: log.blockNumber,
          txHash: log.transactionHash
        })
      }
    }
  }

  const merged = [...(cached?.sales ?? []), ...sales]
  // One entry per event; a re-scan of an overlapping window must not double
  // it, and a listing id must never collide with an offer id that happens to
  // share the number.
  const unique = Array.from(new Map(merged.map((s) => [`${s.txHash}-${s.source}-${s.sourceId}`, s])).values())
  unique.sort((x, y) => y.blockNumber - x.blockNumber)

  writeCache(params.marketplaceAddress, {
    lastScannedBlock: latest,
    sales: unique
  })

  return { sales: unique, truncated, scannedFrom: floor }
}
